/**
 * TanStack Query wrappers for the cloud alarm-rule authoring UI.
 *
 * Mirrors the pattern in OverviewPage / SchedulePage: every page-level
 * query is a thin `useQuery` over the active adapter so the same hooks
 * work in `mock` and `tb` modes without conditionals at the call site.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAdapter } from "@/store/site";
import type {
  AlarmRuleEnvelope,
  AlarmRulesStatus,
  AvailableDatapointsEnvelope,
} from "@/data/alarmRules/wireFormat";
import type { AlarmTargetableDevice } from "@/data/adapter";

const RULES_KEY = (deviceId: string) => ["alarm-rules", deviceId] as const;
const STATUS_KEY = (deviceId: string) => ["alarm-rules-status", deviceId] as const;
const DATAPOINTS_KEY = (deviceId: string) => ["alarm-rules-datapoints", deviceId] as const;
const DEVICES_KEY = ["alarm-rules-targetable-devices"] as const;

export function useAlarmRules(deviceId: string | null) {
  const adapter = useAdapter();
  return useQuery<AlarmRuleEnvelope>({
    queryKey: RULES_KEY(deviceId ?? ""),
    enabled: !!deviceId,
    queryFn: () => adapter.listAlarmRules(deviceId!),
    staleTime: 10_000,
  });
}

export function useAvailableDatapoints(deviceId: string | null) {
  const adapter = useAdapter();
  return useQuery<AvailableDatapointsEnvelope>({
    queryKey: DATAPOINTS_KEY(deviceId ?? ""),
    enabled: !!deviceId,
    queryFn: () => adapter.getAvailableDatapoints(deviceId!),
    staleTime: 30_000,
  });
}

export function useAlarmRulesStatus(
  deviceId: string | null,
  /**
   * Poll cadence in ms. Pass `false` to disable polling once the ACK
   * has landed; the page wires this from "pending" / "acknowledged"
   * state.
   */
  refetchInterval: number | false = 2000,
) {
  const adapter = useAdapter();
  return useQuery<AlarmRulesStatus | null>({
    queryKey: STATUS_KEY(deviceId ?? ""),
    enabled: !!deviceId,
    queryFn: () => adapter.getAlarmRulesStatus(deviceId!),
    refetchInterval,
    staleTime: 0,
  });
}

export function useTargetableDevices() {
  const adapter = useAdapter();
  return useQuery<AlarmTargetableDevice[]>({
    queryKey: DEVICES_KEY,
    queryFn: () => adapter.listTargetableDevices(),
    staleTime: 60_000,
  });
}

export interface SaveAlarmRulesVars {
  deviceId: string;
  envelope: AlarmRuleEnvelope;
}

export function useSaveAlarmRules() {
  const adapter = useAdapter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: SaveAlarmRulesVars) => {
      await adapter.saveAlarmRules(vars.deviceId, vars.envelope);
      return vars;
    },
    onSuccess: (vars) => {
      qc.invalidateQueries({ queryKey: RULES_KEY(vars.deviceId) });
      qc.invalidateQueries({ queryKey: STATUS_KEY(vars.deviceId) });
    },
  });
}
